import { memo, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import axios from "axios";
import { FaApple } from 'react-icons/fa';
import { BiLogoPlayStore } from "react-icons/bi";
import logo from "../logo.svg";
import shuchilogo from '../assets/images/shuchiai.png';
import '../assets/css/featured-apps.css';

const API_BASE = process.env.NODE_ENV === "production" ? "https://shuchiai.com" : "";

const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const PhoneMockup = memo(function PhoneMockup({ rawUrls, isSectionVisible }) {
  const [current, setCurrent] = useState(0);
  const cardRef = useRef(null);
  const swiperRef = useRef(null);
  const [isCardVisible, setIsCardVisible] = useState(false);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsCardVisible(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(card);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || rawUrls.length <= 1 || !isSectionVisible || !isCardVisible) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (!swiper.destroyed) {
        swiper.slideNext(500);
      }
    }, 2000);

    return () => window.clearInterval(timer);
  }, [isSectionVisible, isCardVisible, rawUrls.length]);

  return (
    <div className="fa-card-left" ref={cardRef}>
      <div className="fa-left-ticks">
        {rawUrls.map((_, i) => (
          <div key={i} className={`fa-tick ${current === i ? "active" : ""}`}></div>
        ))}
      </div>
      <div className="fa-phone-inner">
        <div className="fa-phone-screen">
          {rawUrls.length > 0 ? (
            <Swiper
              grabCursor={true}
              slidesPerView={1}
              loop={rawUrls.length > 1}
              speed={500}
              className="swiper-mockup"
              onSwiper={(swiper) => { swiperRef.current = swiper; }}
              onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
              style={{ width: '100%', height: '100%', borderRadius: '28px' }}
            >
              {rawUrls.map((url, i) => (
                <SwiperSlide key={i}>
                  <img
                    src={url}
                    alt="screenshot"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div style={{ width: '100%', height: '100%', backgroundColor: '#d1d1d6' }} />
          )}
        </div>
      </div>
    </div>
  );
});

export default function MobileOnlySlider() {
  const sectionRef = useRef(null);
  const [apps, setApps] = useState([]);
  const [isSectionVisible, setIsSectionVisible] = useState(false);

  useEffect(() => {
    axios
      .get(API_BASE + "/lulto_admin/api/apps")
      .then((res) => {
        const appsData = Array.isArray(res.data) ? res.data : (res.data?.apps || []);
        setApps(appsData);
      })
      .catch((err) => {
        console.error("API Error:", err);
      });
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsSectionVisible(entry.isIntersecting),
      { threshold: 0.01 }
    );
    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="featured-apps-section"
      id='our-apps'
    >
      <div className="fa-sticky-background" aria-hidden="true" />
      <div className="fa-container">
        <div className="fa-header">
          <span className="about-badge">Featured Apps</span>
          <h2 className="fa-title">
            Everything You Need,<br />
            One Collection.
          </h2>
          <p className="fa-description">
            Discover a collection of smart, intuitive apps designed to simplify everyday tasks and enhance your digital lifestyle. Experience innovation, performance, and convenience—all in one place.
          </p>
        </div>

        {Array.isArray(apps) && apps.map((app, index) => {
          const rawUrls = app.imageUrls || [];

          const renderContent = () => (
            <div className="fa-card-right">
              <div className="fa-app-header">
                <div className="fa-app-logo">
                  <img src={app.logo || (index === 0 ? shuchilogo : logo)} alt="App Logo" />
                </div>
                {app.websiteUrl && (
                  <a href={app.websiteUrl} target="_blank" rel="noopener noreferrer" className="fa-globe-icon" aria-label="Website">
                    <GlobeIcon />
                  </a>
                )}
              </div>

              <h3 className="fa-app-title">{app.title}</h3>
              <p className="fa-app-desc">{app.description}</p>

              {/* <div className="fa-store-buttons">
                {app.webStoreUrl && (
                  <a href={app.webStoreUrl} target="_blank" rel="noopener noreferrer" className="fa-play-store-btn">
                    Play Store <ArrowUpRightIcon />
                  </a>
                )}
                {app.appStoreUrl && (
                  <a href={app.appStoreUrl} target="_blank" rel="noopener noreferrer" className="fa-play-store-btn">
                    App Store <ArrowUpRightIcon />
                  </a>
                )}
              </div> */}

              <div className="home-hero-store-btns">
                {app.webStoreUrl && (
                  <button
                    className="header-contact-btn home-store-btn"
                    onClick={() => window.open(app.webStoreUrl, "_blank", "noopener,noreferrer")}
                  >
                    <BiLogoPlayStore size={20} />
                    Play Store
                  </button>
                )}

                {app.appStoreUrl && (
                  <button
                    className="header-contact-btn home-store-btn"
                    onClick={() => window.open(app.appStoreUrl, "_blank", "noopener,noreferrer")}
                  >
                    <FaApple size={20} />
                    App Store
                  </button>
                )}
              </div>
            </div>
          );

          return (
            <div key={app._id || index} className="fa-card-wrapper sticky-stack-card" style={{ zIndex: index + 1 }}>
              <PhoneMockup rawUrls={rawUrls} isSectionVisible={isSectionVisible} />
              {renderContent()}
            </div>
          );
        })}
      </div>
    </section>
  );
}
